import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  InferenceForm,
  InferenceParameters,
  StyleCapabilities,
} from "../graphql/customTypes";
import {
  GenerationType,
  GuidanceImageType,
  InferenceQuality,
} from "../graphql/schema.graphql";
import ForgeIcon from "../ui/icons/ForgeIcon";

export function guidanceScaleToSlider(scale: number) {
  // normalize guidance scale ranges [0, 7, 13] to [0, 50, 100]
  let creativity = 0;
  if (scale < 7) {
    creativity = Math.round(50 * (scale / 7));
  } else {
    creativity = Math.round(50 + 50 * ((scale - 7) / 6));
  }
  return Math.round(100 - creativity);
}

export function guidanceScaleFromSlider(slider: number) {
  const creativity = Math.round(100 - slider);
  // normalize creativity ranges [0, 50, 100] to [0, 7, 13]
  const lowerPart = creativity < 50 ? (creativity / 50) * 7 : 7;
  const upperPart = creativity > 50 ? ((creativity - 50) / 50) * 6 : 0;
  return Math.round((lowerPart + upperPart) / 0.05) * 0.05;
}

export function imageWeightToSimilaritySlider(scale: number) {
  return Math.round(scale * 100);
}

export function imageWeightFromSimilaritySlider(slider: number) {
  return Math.round((slider / 100) * 20) / 20;
}

export function pixelDensityFromSlider(slider: number) {
  return Math.round(Math.min(Math.max(slider, 1), 4) / 0.5) * 0.5;
}

export function pixelDensityToSlider(pixelDensity: number) {
  if (!pixelDensity) return 1;
  return Math.min(Math.max(pixelDensity, 1), 4);
}

export function getForgeUrl({
  workspaceName,
  inputs,
  addStyle = false,
  disableTour = false,
}: {
  workspaceName: string;
  inputs?: DeepPartial<InferenceForm>;
  addStyle?: boolean;
  disableTour?: boolean;
}) {
  const params = new URLSearchParams();
  if (inputs) {
    params.append("inputs", JSON.stringify(inputs));
  }
  if (addStyle) {
    params.append("addStyle", "true");
  }
  if (disableTour) {
    params.append("disableTour", "true");
  }
  return `/${workspaceName}/forge?${params.toString()}`;
}

export function getForgeSessionUrl({
  workspaceName,
  forgeSessionId,
  inputs,
  addStyle = false,
  disableTour = false,
}: {
  workspaceName: string;
  forgeSessionId: string;
  inputs?: DeepPartial<InferenceForm>;
  addStyle?: boolean;
  disableTour?: boolean;
}) {
  const params = new URLSearchParams();
  if (inputs) {
    params.append("inputs", JSON.stringify(inputs));
  }
  if (addStyle) {
    params.append("addStyle", "true");
  }
  if (disableTour) {
    params.append("disableTour", "true");
  }
  return `/${workspaceName}/forge-session/${forgeSessionId}?${params.toString()}`;
}

export function isImageTypeValid(
  imageType: GuidanceImageType,
  capabilities: StyleCapabilities,
  allowedImageTypes?: GuidanceImageType[],
  params?: InferenceParameters,
  currentImageType?: GuidanceImageType,
) {
  if (params && currentImageType && currentImageType === imageType) {
    return true;
  }
  if (
    params &&
    params.images &&
    params.images.some((paramImage) => paramImage.type === imageType)
  ) {
    return false;
  }
  if (allowedImageTypes && !allowedImageTypes.includes(imageType)) {
    return false;
  }
  return capabilities?.imageTypes?.includes(imageType);
}

export const generationTypes = [
  {
    type: GenerationType.Create,
    actionName: "Forge",
    tooltip: "Generate images inside the selected area",
    icon: (
      <ForgeIcon
        weight="solid"
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
  {
    type: GenerationType.Refill,
    actionName: "Refill",
    tooltip: "Refill transparent pixels",
    icon: (
      <FontAwesomeIcon
        icon={solid("diamond-half-stroke")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
  {
    type: GenerationType.Edit,
    actionName: "Edit",
    tooltip: "Edit the image using a text prompt",
    icon: (
      <FontAwesomeIcon
        icon={solid("pencil")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
];

export const generationTypesMap = generationTypes.reduce(
  (acc, type) => ({ ...acc, [type.type]: type }),
  {},
) as Record<GenerationType, (typeof generationTypes)[0]>;

export const inferenceQualities = [
  {
    type: InferenceQuality.High,
    actionName: "High",
    tooltip: "Higher quality but slower generation",
    icon: (
      <FontAwesomeIcon
        icon={solid("gauge-simple-min")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
  {
    type: InferenceQuality.Medium,
    actionName: "Medium",
    tooltip: "Balanced quality and speed",
    icon: (
      <FontAwesomeIcon
        icon={solid("gauge-simple")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
  {
    type: InferenceQuality.Low,
    actionName: "Low",
    tooltip: "Lower quality but faster generation",
    icon: (
      <FontAwesomeIcon
        icon={solid("gauge-simple-max")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
];

export const inferenceQualitiesMap = inferenceQualities.reduce(
  (acc, type) => ({ ...acc, [type.type]: type }),
  {},
) as Record<InferenceQuality, (typeof inferenceQualities)[0]>;

export const transparencyOptions = [
  {
    value: false,
    actionName: "Non-Transparent",
    tooltip: "Generate images with no transparency",
    icon: (
      <FontAwesomeIcon
        icon={regular("image")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
  {
    value: true,
    actionName: "Transparent",
    tooltip: "Generate images with transparency",
    icon: (
      <FontAwesomeIcon
        icon={solid("image")}
        className="cursor-pointer relative z-[1] pointer-events-none"
      />
    ),
  },
];

export const transparencyOptionsMap = transparencyOptions.reduce(
  (acc, option) => ({ ...acc, [option.value.toString()]: option }),
  {},
) as Record<string, (typeof transparencyOptions)[0]>;
